// The in-place workspace chat surface for one customer-anchored thread.
//
// Renders the persisted transcript (GET /threads/:id) + this session's
// completed turns + the live streaming turn — all through the same ChatMessage
// renderer, so "reload → identical transcript" holds. The scoped composer sends
// `thread_id` so every turn files into THIS thread (manual selection = 100%
// accurate anchoring, the safety net behind auto-anchoring).
//
// No mid-session refetch: messages persist in a post-response waitUntil on the
// worker (after the SSE `done`), so an immediate refetch would race the write.
// Instead we keep completed turns in local session state; a real reload re-reads
// the now-persisted transcript from the DB.

import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowUp, Flag, Paperclip, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { dispatchEricStream, type EricPhotoContext } from '@/lib/eric-stream'
import { useStagedAttachment } from '@/lib/staged-attachment'
import { parseReportPrefix } from '@/lib/parse'
import { submitReport } from '@/lib/eric-reports'
import {
  fetchThread,
  rowToVM,
  threadKeys,
  toolChipDetail,
  type ChatVM,
  type UserVoiceVM,
} from '@/lib/threads'
import { consumePendingVoiceClip } from '@/lib/pending-voice'
import { VoiceMicButton, type VoiceHintKind } from '@/components/voice/VoiceMicButton'
import type { StoppedClip } from '@/components/voice/useMediaRecorder'
import { ChatMessage } from './messages'
import { RecordPanel } from './RecordPanel'
import { ThreadCascade } from './ThreadCascade'

type LiveTurn = {
  prompt: string
  tools: string[]
  text: string
  imageUrl?: string
  // Set for a voice-originated turn: the bubble renders the player (local
  // blob URL) immediately, with `transcribing` naming the pre-dispatch step.
  voice?: UserVoiceVM
}

// Mirror of worker/routes/eric-voice.ts extensionFor — names the uploaded
// file so the R2 key stays human-debuggable.
function extensionFor(mimeType: string): string {
  if (mimeType.startsWith('audio/webm')) return 'webm'
  if (mimeType.startsWith('audio/mp4')) return 'm4a'
  if (mimeType === 'audio/mpeg') return 'mp3'
  if (mimeType.startsWith('audio/ogg')) return 'ogg'
  return 'bin'
}

type TranscribeResponse = {
  voice_message_id: string
  transcript: string
  duration_ms: number
  audio_url: string
}

export function ThreadChat({
  threadId,
  customerName,
}: {
  threadId: string
  customerName: string | null
}) {
  const { t } = useTranslation('dashboard')
  const { t: tVoice } = useTranslation('voice_messages')
  const { getToken, isLoaded } = useAuth()
  const queryClient = useQueryClient()

  // The mounted session owns the transcript: persisted rows load once, then
  // completed turns accrete in local state (sessionTurns). An automatic refetch
  // (window focus / reconnect) after the worker's post-response waitUntil has
  // persisted those turns would surface them in BOTH persistedVMs and
  // sessionTurns → duplicates. Pin the query so the only refresh paths are a
  // thread switch (key remount, fresh instance) or a full reload (fresh mount).
  const detail = useQuery({
    queryKey: threadKeys.detail(threadId),
    enabled: isLoaded,
    queryFn: () => fetchThread(getToken, threadId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const [sessionTurns, setSessionTurns] = useState<ChatVM[]>([])
  const [liveTurn, setLiveTurn] = useState<LiveTurn | null>(null)
  const [value, setValue] = useState('')
  const [sending, setSending] = useState(false)
  // Ref mirror of `sending` for callbacks that fire from stale closures — the
  // recorder's onstop captures the render current at pointerdown, so a voice
  // clip delivered seconds later must read the CURRENT lock, not the captured
  // one (otherwise a voice send can clobber an in-flight text turn).
  const sendingRef = useRef(false)
  const updateSending = (v: boolean) => {
    sendingRef.current = v
    setSending(v)
  }
  // A photo the mechanic attached but hasn't sent yet (e.g. a VIN sticker to
  // add to this thread's vehicle). Staged via the same hook as TaskEricInput:
  // attach → downscale → background classify (R2 + Sonnet vision) → Send ships
  // the EXTRACTION to Eric, never pixels (locked 2026-06-10). This surface
  // renders no suggestion chips — the hook works without them.
  const stagedAttachment = useStagedAttachment({ getToken })
  const staged = stagedAttachment.staged
  // Hold-Send expired while the photo was still being read — calm hint.
  const [stillReading, setStillReading] = useState(false)
  // Transient voice hint (too-short press, mic denied, transcribe failure) —
  // same calm inline slot the report-mode hint uses, auto-cleared.
  const [voiceHint, setVoiceHint] = useState<VoiceHintKind | 'rate_limited' | null>(null)
  const voiceHintTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const showVoiceHint = (kind: VoiceHintKind | 'rate_limited') => {
    setVoiceHint(kind)
    if (voiceHintTimer.current) clearTimeout(voiceHintTimer.current)
    voiceHintTimer.current = setTimeout(() => setVoiceHint(null), 3000)
  }

  const turnSeq = useRef(0)
  // The last completed Eric turn, so a following `report:` anchors to exactly
  // what's on Emil's screen (set in onDone). null until the first turn lands —
  // the worker then falls back to the user's most recent Eric turn.
  const lastTurnRef = useRef<{
    requestId: string | null
    userMessage: string
    ericReply: string
  } | null>(null)
  const liveToolsRef = useRef<string[]>([])
  const abortRef = useRef<AbortController | null>(null)
  // Every voice object URL this component mints (one per recorded clip). All
  // revoked on unmount: sessionTurns dies with the component (thread switch
  // remounts via key={threadId}), so nothing can replay them afterward — and
  // without the revoke each ~100-200 KB blob stays pinned until page reload,
  // unbounded across a day of voice notes.
  const voiceUrlsRef = useRef<string[]>([])
  useEffect(() => {
    const urls = voiceUrlsRef.current
    return () => {
      for (const u of urls) URL.revokeObjectURL(u)
    }
  }, [])
  // Follow the conversation only while the mechanic is reading at the bottom.
  // The page (not a nested box) is the scroller here, so "at bottom" is a
  // window measurement; 160px ≈ the sticky composer plus a line of slack.
  // Within that band the newest line is on screen and following is what the
  // reader expects; beyond it they've scrolled up on purpose, and a streamed
  // delta must never yank them back (Emil, 2026-07-23 walkthrough: "the
  // thread keeps scrolling up" while a voice turn processed). Content growth
  // alone fires no scroll event, so a true stick survives between pins; a
  // pin lands at distance 0 and re-computes to true — no feedback loop.
  const stickRef = useRef(true)
  useEffect(() => {
    const onScroll = () => {
      stickRef.current =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 160
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const onPickFile = (file: File | null) => {
    if (!file) return
    setStillReading(false)
    stagedAttachment.stage(file)
    // Reset so re-picking the same file fires onChange again.
    if (fileInputRef.current) fileInputRef.current.value = ''
  }
  const clearPendingImage = () => {
    setStillReading(false)
    stagedAttachment.clear()
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // A deterministic Cascade action (book / note / vehicle) finished — append its
  // committed VMs to the SAME sessionTurns the Eric onDone path writes, so live
  // and reloaded transcripts are identical.
  const commitCascade = (vms: ChatVM[]) => {
    stickRef.current = true
    setSessionTurns((prev) => [...prev, ...vms])
  }

  // Switching threads remounts this component (key={threadId} in HomeV2), so
  // session state resets naturally — no reset-on-change effect needed. This only
  // aborts any in-flight stream when the thread is left / the view unmounts.
  // (The staged-attachment hook revokes its own object URL on unmount.)
  useEffect(
    () => () => {
      abortRef.current?.abort()
      if (voiceHintTimer.current) clearTimeout(voiceHintTimer.current)
    },
    [],
  )

  const persistedVMs: ChatVM[] = (detail.data?.messages ?? [])
    .map(rowToVM)
    .filter((vm): vm is ChatVM => vm !== null)

  const liveVMs: ChatVM[] = []
  if (liveTurn) {
    liveVMs.push({
      kind: 'user',
      id: 'live-user',
      text: liveTurn.prompt,
      imageUrl: liveTurn.imageUrl,
      voice: liveTurn.voice,
    })
    liveTurn.tools.forEach((tn, i) =>
      liveVMs.push({ kind: 'tool', id: `live-tool-${i}`, tool: tn }),
    )
    // During transcription Eric hasn't started — the voice bubble names that
    // step itself; a "thinking" Eric bubble here would be theater.
    if (!liveTurn.voice?.transcribing) {
      liveVMs.push({ kind: 'eric', id: 'live-eric', text: liveTurn.text, streaming: true })
    }
  }

  const allVMs = [...persistedVMs, ...sessionTurns, ...liveVMs]

  // Instant (non-smooth) pins: this fires per SSE delta while Eric streams,
  // and restarting a smooth animation that often reads as bouncing. Pin by
  // scrolling the window to its clamped maximum — NOT scrollIntoView on a
  // bottom sentinel: the sentinel's aligned position sits a composer-stack
  // (~155-250px of sticky-composer flow height plus main's bottom padding)
  // ABOVE the true bottom, so a sentinel pin yanks a bottom-resting reader
  // upward and leaves the stick listener reading false (#242 review,
  // browser-measured). scrollTo clamps, so distance lands at exactly 0.
  useEffect(() => {
    if (!stickRef.current) return
    window.scrollTo(0, document.documentElement.scrollHeight)
  }, [allVMs.length, liveTurn?.text])

  const submit = async () => {
    const prompt = value.trim()
    if ((!prompt && !staged) || sending) return

    // `report:` — divert to the problem-report sink, never to Eric. Text-only;
    // a pending photo (if any) stays attached for a later real turn.
    const report = parseReportPrefix(prompt)
    if (report.isReport) {
      setValue('')
      updateSending(true)
      // Committed send — re-engage following even from up-thread. Set at the
      // commit points, not at submit() entry: a staged photo that isn't ready
      // aborts the send below and must not arm a pin.
      stickRef.current = true
      try {
        const res = await submitReport(getToken, {
          text: report.text,
          threadId,
          surface: 'thread',
          anchor: lastTurnRef.current
            ? {
                request_id: lastTurnRef.current.requestId,
                user_message: lastTurnRef.current.userMessage,
                eric_reply: lastTurnRef.current.ericReply,
              }
            : null,
        })
        const n = turnSeq.current++
        const vm = res.message ? rowToVM(res.message) : null
        setSessionTurns((prev) => [
          ...prev,
          vm ?? { kind: 'report', id: `s${n}-report`, text: report.text },
        ])
        queryClient.invalidateQueries({ queryKey: threadKeys.rail() })
      } catch {
        const n = turnSeq.current++
        setSessionTurns((prev) => [
          ...prev,
          { kind: 'system', id: `s${n}-report-err`, text: t('home_v2.report_failed') },
        ])
      } finally {
        updateSending(false)
      }
      return
    }

    // Consume the staged photo's extraction first (holds ≤3s for an in-flight
    // classify). `unreadable` / `pending` keep the photo staged and abort the
    // send — the status text / hint explain; nothing dispatches.
    let photo: EricPhotoContext | undefined
    let bubbleUrl: string | undefined
    if (staged) {
      updateSending(true)
      const file = staged.file
      const res = await stagedAttachment.consumeForSend()
      if (res.kind !== 'ready') {
        updateSending(false)
        if (res.kind === 'pending') setStillReading(true)
        return
      }
      setStillReading(false)
      photo = res.photo
      // The hook revokes its preview URL on consume — mint a fresh one for the
      // sent turn's bubble (same leave-it-alive contract as before).
      bubbleUrl = URL.createObjectURL(file)
    }

    setValue('')
    updateSending(true)
    stickRef.current = true
    liveToolsRef.current = []
    setLiveTurn({ prompt, tools: [], text: '', imageUrl: bubbleUrl })
    if (fileInputRef.current) fileInputRef.current.value = ''
    abortRef.current?.abort()
    const ac = new AbortController()
    abortRef.current = ac

    try {
      await dispatchEricStream({
        prompt,
        threadId,
        photo,
        getToken,
        signal: ac.signal,
        handlers: {
          onTool: (name) => {
            liveToolsRef.current = [...liveToolsRef.current, name]
            setLiveTurn((p) => (p ? { ...p, tools: liveToolsRef.current } : p))
          },
          onText: (delta) =>
            setLiveTurn((p) => (p ? { ...p, text: p.text + delta } : p)),
          onDone: (envelope) => {
            // Remember this turn for a possible follow-up `report:`.
            lastTurnRef.current = {
              requestId: envelope.request_id,
              userMessage: prompt,
              ericReply: envelope.reply ?? '',
            }
            const n = turnSeq.current++
            const committed: ChatVM[] = [
              { kind: 'user', id: `s${n}-user`, text: prompt, imageUrl: bubbleUrl },
            ]
            // Enrich the committed chips with the entity detail from the now-
            // resolved focus (the streaming chips were name-only — focus isn't
            // known until `done`). Same source as the persisted card, so this
            // matches the reloaded transcript.
            liveToolsRef.current.forEach((tn, i) =>
              committed.push({
                kind: 'tool',
                id: `s${n}-tool-${i}`,
                tool: tn,
                detail: toolChipDetail(tn, envelope.focus),
              }),
            )
            if (envelope.reply && envelope.reply.trim().length > 0) {
              committed.push({ kind: 'eric', id: `s${n}-eric`, text: envelope.reply })
            }
            if (envelope.mode === 'executed' && envelope.result) {
              committed.push({
                kind: 'result',
                id: `s${n}-result`,
                result: envelope.result,
                intent: envelope.intent,
                requestId: envelope.request_id,
                undoable: true,
                undoneAt: null,
                createdAt: new Date().toISOString(),
              })
            }
            setSessionTurns((prev) => [...prev, ...committed])
            setLiveTurn(null)
            // Bubble the thread up the rail (best-effort; recency persists post-response).
            queryClient.invalidateQueries({ queryKey: threadKeys.rail() })
          },
        },
      })
    } catch (err) {
      if ((err as { name?: string } | null)?.name === 'AbortError') return
      const n = turnSeq.current++
      setSessionTurns((prev) => [
        ...prev,
        { kind: 'user', id: `s${n}-user`, text: prompt, imageUrl: bubbleUrl },
        { kind: 'system', id: `s${n}-err`, text: t('home_v2.send_failed') },
      ])
      setLiveTurn(null)
    } finally {
      updateSending(false)
    }
  }

  // POST the clip for transcription. Throws 'rate_limited' /
  // 'transcribe_failed' (or an AbortError from the signal) — the callers map
  // those to the hint + the audio-stays commit.
  const transcribeClip = async (
    clip: StoppedClip,
    signal: AbortSignal,
  ): Promise<TranscribeResponse> => {
    const token = await getToken()
    if (!token) throw new Error('transcribe_failed')
    const form = new FormData()
    form.append('audio', clip.blob, `voice.${extensionFor(clip.mimeType)}`)
    form.append('duration_ms', String(Math.max(0, Math.round(clip.durationMs))))
    form.append('source', 'web')
    const res = await fetch('/api/eric/voice/transcribe', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
      signal,
    })
    if (!res.ok) {
      throw new Error(res.status === 429 ? 'rate_limited' : 'transcribe_failed')
    }
    return (await res.json()) as TranscribeResponse
  }

  // Quick-press / locked send: the recording IS the message. The bubble lands
  // in the thread immediately (local blob, playable right away) and passes
  // through named steps — Transcribing… → Eric streaming — so a slow turn
  // never reads as a failure. The worker re-reads the transcript server-side
  // and persists the turn with the replayable card (reload-identical).
  const submitVoice = async (clip: StoppedClip) => {
    // Ref, not state: this runs from the recorder's onstop, whose closure was
    // captured at pointerdown — the state value could be seconds stale.
    if (sendingRef.current) return
    updateSending(true)
    stickRef.current = true
    const audioUrl = URL.createObjectURL(clip.blob)
    voiceUrlsRef.current.push(audioUrl)
    const durationMs = Math.max(0, Math.round(clip.durationMs))
    // Captured outside the try so the failure path can commit the bubble with
    // whatever the turn had at the point of failure.
    let transcript = ''
    let sentVoiceId: string | null = null
    liveToolsRef.current = []
    setLiveTurn({
      prompt: '',
      tools: [],
      text: '',
      voice: { voiceMessageId: null, durationMs, audioUrl, transcribing: true },
    })

    // The controller exists BEFORE the transcribe fetch and covers BOTH
    // requests — switching threads mid-transcription must kill the whole
    // turn, or the dispatch would fire invisibly after unmount (and a
    // re-record in the new thread could execute the same action twice).
    abortRef.current?.abort()
    const ac = new AbortController()
    abortRef.current = ac

    try {
      const stt = await transcribeClip(clip, ac.signal)
      transcript = stt.transcript.trim()
      sentVoiceId = stt.voice_message_id
      const voiceVM: UserVoiceVM = {
        voiceMessageId: stt.voice_message_id,
        durationMs,
        audioUrl,
      }
      setLiveTurn((p) => (p ? { ...p, prompt: transcript, voice: voiceVM } : p))

      await dispatchEricStream({
        prompt: transcript,
        threadId,
        voiceMessageId: stt.voice_message_id,
        getToken,
        signal: ac.signal,
        handlers: {
          onTool: (name) => {
            liveToolsRef.current = [...liveToolsRef.current, name]
            setLiveTurn((p) => (p ? { ...p, tools: liveToolsRef.current } : p))
          },
          onText: (delta) =>
            setLiveTurn((p) => (p ? { ...p, text: p.text + delta } : p)),
          onDone: (envelope) => {
            lastTurnRef.current = {
              requestId: envelope.request_id,
              userMessage: transcript,
              ericReply: envelope.reply ?? '',
            }
            const n = turnSeq.current++
            const committed: ChatVM[] = [
              // Same leave-alive contract as the photo bubble URL: the object
              // URL stays valid for the session so the committed bubble replays
              // without a refetch; a reload streams from the worker proxy.
              { kind: 'user', id: `s${n}-user`, text: transcript, voice: voiceVM },
            ]
            liveToolsRef.current.forEach((tn, i) =>
              committed.push({
                kind: 'tool',
                id: `s${n}-tool-${i}`,
                tool: tn,
                detail: toolChipDetail(tn, envelope.focus),
              }),
            )
            if (envelope.reply && envelope.reply.trim().length > 0) {
              committed.push({ kind: 'eric', id: `s${n}-eric`, text: envelope.reply })
            }
            if (envelope.mode === 'executed' && envelope.result) {
              committed.push({
                kind: 'result',
                id: `s${n}-result`,
                result: envelope.result,
                intent: envelope.intent,
                requestId: envelope.request_id,
                undoable: true,
                undoneAt: null,
                createdAt: new Date().toISOString(),
              })
            }
            setSessionTurns((prev) => [...prev, ...committed])
            setLiveTurn(null)
            queryClient.invalidateQueries({ queryKey: threadKeys.rail() })
          },
        },
      })
    } catch (err) {
      if ((err as { name?: string } | null)?.name === 'AbortError') return
      // The recording is the artifact — keep the bubble on EVERY failure
      // class (transcribe HTTP error, dispatch fault), with an honest system
      // line under it ("audio stays"). The hint names the reason.
      const reason = (err as Error | null)?.message
      showVoiceHint(reason === 'rate_limited' ? 'rate_limited' : 'failed')
      const n = turnSeq.current++
      setSessionTurns((prev) => [
        ...prev,
        {
          kind: 'user',
          id: `s${n}-user`,
          text: transcript,
          voice: { voiceMessageId: sentVoiceId, durationMs, audioUrl },
        },
        { kind: 'system', id: `s${n}-err`, text: t('home_v2.send_failed') },
      ])
      setLiveTurn(null)
    } finally {
      updateSending(false)
    }
  }

  // Locked "Use as text" — today's dictation, kept: the transcript lands in
  // the textarea for editing and sends as a normal TYPED turn (no voice card;
  // the unclaimed clip ages out with the 90-day R2 prune).
  // A clip recorded on the dock (Today's composer) rides in via the
  // pending-voice slot and runs through the normal voice path, so dock
  // voice and in-thread voice are literally the same pipeline (transcribe
  // → dispatch → bubbles → scroll contract). The slot clears on read, which
  // is the whole once-guard — it covers StrictMode's double effect run AND
  // a real leave-and-return remount, so post-first-run this effect is a
  // null-check per render.
  useEffect(() => {
    const clip = consumePendingVoiceClip(threadId)
    // Microtask: submitVoice's first writes (sending / liveTurn) are
    // synchronous, and the strict react-hooks gate forbids sync setState in
    // an effect body (cascading-render rule). Deferring one tick changes
    // nothing user-visible — and no cancel handle on purpose: a StrictMode
    // cleanup must not eat the one-shot submit.
    if (clip) queueMicrotask(() => void submitVoice(clip))
  })

  const submitClipToText = async (clip: StoppedClip) => {
    if (sendingRef.current) return
    updateSending(true)
    abortRef.current?.abort()
    const ac = new AbortController()
    abortRef.current = ac
    try {
      const stt = await transcribeClip(clip, ac.signal)
      const transcript = stt.transcript.trim()
      if (!transcript) {
        // Nothing usable came back — the box would stay empty either way;
        // the too-short-style hint tells the mechanic to go again.
        showVoiceHint('failed')
        return
      }
      setValue((prev) => (prev.trim() ? `${prev.trimEnd()} ${transcript}` : transcript))
      textareaRef.current?.focus()
    } catch (err) {
      if ((err as { name?: string } | null)?.name === 'AbortError') return
      const reason = (err as Error | null)?.message
      showVoiceHint(reason === 'rate_limited' ? 'rate_limited' : 'failed')
    } finally {
      updateSending(false)
    }
  }

  const placeholder = customerName
    ? t('home_v2.composer_scoped', { name: customerName })
    : t('home_v2.composer_unscoped')

  // Live "report mode": the message starts with `report:`, so this turn logs a
  // problem instead of going to Eric. Drives the calm hint + the send restyle.
  const reportMode = parseReportPrefix(value).isReport

  // Deterministic Cascade is available only on a filed thread (customer known).
  const cascadeCustomerId = detail.data?.thread.customer_id ?? null
  const cascadeVehicle = detail.data?.record.vehicle ?? null

  return (
    <div className="flex flex-col gap-4">
      {detail.data ? (
        <RecordPanel thread={detail.data.thread} record={detail.data.record} />
      ) : null}

      <div className="min-h-[8rem] space-y-3 pb-3">
        {detail.isLoading ? (
          <div className="space-y-3">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="h-8 w-2/3 animate-pulse rounded-2xl bg-muted/50 motion-reduce:animate-none"
              />
            ))}
          </div>
        ) : allVMs.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('home_v2.thread_empty')}
          </p>
        ) : (
          allVMs.map((vm) => <ChatMessage key={vm.id} vm={vm} />)
        )}
      </div>

      {/* Scoped composer — sticky on mobile, respects the home indicator. */}
      <div className="sticky bottom-[var(--shell-bottom-inset,0px)] -mx-4 border-t border-border bg-background px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:static md:mx-0 md:border-t-0 md:bg-transparent md:px-0">
        {/* Deterministic Cascade quick-actions, scoped to this filed thread's
            customer — book / add note / add vehicle without going through Eric.
            The freeform Eric composer below stays for open-ended turns. */}
        {customerName && cascadeCustomerId ? (
          <div className="mb-2">
            <ThreadCascade
              threadId={threadId}
              customerId={cascadeCustomerId}
              customerName={customerName}
              vehicleId={cascadeVehicle?.id ?? null}
              vehicleLabel={cascadeVehicle?.label ?? null}
              hasVehicle={!!cascadeVehicle}
              onCommitted={commitCascade}
            />
          </div>
        ) : null}
        <div className="rounded-2xl border border-border bg-card px-3 py-2 focus-within:border-muted-foreground/40">
          {/* Report mode — the message starts with `report:`, so it logs a
              problem instead of going to Eric. Calm, not an error. */}
          {reportMode ? (
            <div
              className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-150 ease-out motion-reduce:animate-none"
              aria-live="polite"
            >
              <Flag className="size-3 shrink-0" />
              <span>{t('home_v2.report_mode_hint')}</span>
            </div>
          ) : null}
          {/* Transient voice hint (too-short press, mic denied, failure) —
              same calm slot as the report hint, auto-cleared. */}
          {voiceHint ? (
            <div
              className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-150 ease-out motion-reduce:animate-none"
              aria-live="polite"
            >
              <span>{tVoice(`hint_${voiceHint}`)}</span>
            </div>
          ) : null}
          {/* Pending photo (e.g. a VIN sticker) — thumbnail + clear, above the
              input row, before it's sent into the conversation. */}
          {staged ? (
            <div className="mb-2 flex items-center gap-2">
              <img
                src={staged.previewUrl}
                alt={t('home_v2.photo_attached')}
                className="size-12 rounded-lg border border-border object-cover"
              />
              <span
                className={
                  staged.status === 'error'
                    ? 'text-xs text-destructive'
                    : 'text-xs text-muted-foreground'
                }
                aria-live="polite"
              >
                {staged.status === 'analyzing'
                  ? stillReading
                    ? t('agentic.attach.staged.still_reading')
                    : t('agentic.attach.staged.analyzing')
                  : staged.status === 'error'
                    ? t('agentic.attach.staged.error')
                    : t('home_v2.photo_ready')}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="pill-icon-sm"
                onClick={clearPendingImage}
                aria-label={t('home_v2.photo_remove')}
                className="ml-auto text-muted-foreground"
              >
                <X className="size-4" />
              </Button>
            </div>
          ) : null}
          {/* `relative` anchors the mic's recording strip, which overlays this
              row while the mic is hot. */}
          <div className="relative flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            />
            <Button
              type="button"
              variant="ghost"
              size="pill-icon"
              onClick={() => fileInputRef.current?.click()}
              aria-label={t('home_v2.attach_photo')}
              className="shrink-0 text-muted-foreground"
            >
              <Paperclip className="size-4" />
            </Button>
            <Textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void submit()
                }
              }}
              rows={1}
              // Lock the box while sending: the staged-photo Send holds ≤3s for
              // the classify, and the prompt was captured at submit() entry — any
              // edit during the hold would be silently wiped by setValue('').
              readOnly={sending}
              placeholder={placeholder}
              aria-label={placeholder}
              className="max-h-32 min-h-0 flex-1 resize-none border-0 bg-transparent px-0 py-1.5 text-base leading-relaxed text-foreground focus-visible:ring-0"
            />
            <VoiceMicButton
              disabled={sending || detail.isLoading}
              onClip={(clip) => void submitVoice(clip)}
              onClipToText={(clip) => void submitClipToText(clip)}
              onHint={showVoiceHint}
            />
            <Button
              type="button"
              variant={reportMode ? 'outline' : 'accent'}
              size="pill-icon"
              onClick={() => void submit()}
              disabled={(!value.trim() && !staged) || sending}
              aria-label={reportMode ? t('home_v2.report_send') : t('home_v2.send')}
              className="shrink-0 disabled:opacity-40"
            >
              {reportMode ? <Flag className="size-4" /> : <ArrowUp className="size-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
